import { ApiError } from '@wa/shared';

/** The form vocabulary the Shop screens share — the same classes Billing uses. */
export const input = 'w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small disabled:opacity-60';
export const primary = 'tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40';
export const secondary = 'tap rounded border border-(--color-line) px-3 py-1.5 text-small font-medium disabled:opacity-40';
export const danger = 'tap rounded border border-(--color-bad) px-3 py-1.5 text-small font-medium text-(--color-bad) disabled:opacity-40';

export function Field({ label, hint, className, children }: {
  label: string; hint?: string; className?: string; children: React.ReactNode;
}) {
  return (
    <label className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</span>
      {children}
      {hint && <span className="text-micro leading-snug text-(--color-muted)">{hint}</span>}
    </label>
  );
}

export function Switch({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-1 size-4 accent-(--color-brand)" />
      <span className="flex flex-col">
        <span className="text-small font-medium">{label}</span>
        {hint && <span className="text-micro leading-snug text-(--color-muted)">{hint}</span>}
      </span>
    </label>
  );
}

export function Card({ title, blurb, children, actions }: {
  title: string; blurb?: string; children: React.ReactNode; actions?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface) p-3 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-body font-semibold">{title}</h2>
          {blurb && <p className="mt-0.5 max-w-prose text-small text-(--color-muted)">{blurb}</p>}
        </div>
        {actions}
      </div>
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </section>
  );
}

export function Notice({ kind, children }: { kind: 'bad' | 'good' | 'muted'; children: React.ReactNode }) {
  const tone = kind === 'bad' ? 'border-(--color-bad) text-(--color-bad)' : kind === 'good' ? 'border-(--color-good) text-(--color-good)' : 'border-(--color-line) text-(--color-muted)';
  return <p role={kind === 'bad' ? 'alert' : 'status'} className={`rounded border px-3 py-2 text-small ${tone}`}>{children}</p>;
}

export function errorText(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : fallback;
}

export function rupees(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return '₹' + value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
